import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

let supabaseInstance: ReturnType<typeof createClient>;
let connectionStatus: 'unknown' | 'ok' | 'failed' = 'unknown';

// Capture the recovery intent BEFORE Supabase processes the URL hash.
// detectSessionInUrl clears the hash during client creation, so we must
// read it at module-load time while it's still intact.
export const isPasswordRecovery = (() => {
  try {
    return window.location.hash.includes('type=recovery');
  } catch {
    return false;
  }
})();

function createDummyClient() {
  const dummyUrl = 'http://localhost';
  const dummyKey = 'dummy-key';
  return createClient(dummyUrl, dummyKey, {
    auth: { autoRefreshToken: false, persistSession: false },
  });
}

// Detect if a request is an auth operation (token refresh, sign in, etc.)
// so we can give it more breathing room and a clearer error message.
function isAuthRequest(url: string | URL | Request): boolean {
  const urlStr = typeof url === 'string' ? url : url instanceof URL ? url.href : (url as Request).url || '';
  return urlStr.includes('/auth/v1/token') || urlStr.includes('/auth/v1/') || urlStr.includes('grant_type=refresh_token');
}

async function fetchWithRetry(
  url: string | URL | Request,
  init?: Parameters<typeof fetch>[1],
  attempt = 1
): Promise<Response> {
  // Auth requests get 30s — token refreshes can be slow on spotty connections.
  // Data requests get 25s — Supabase free tier can be slow on cold starts.
  const isAuth = isAuthRequest(url);
  const timeoutMs = isAuth ? 30000 : 25000;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    clearTimeout(timeoutId);
    connectionStatus = 'ok';

    // Handle JWT clock skew (PGRST303) with one retry after a short delay
    if (res.status === 403 && attempt === 1) {
      const cloned = res.clone();
      try {
        const body = await cloned.json();
        if (
          body?.code === 'PGRST303' ||
          body?.message?.includes('JWT issued at future') ||
          body?.code === 'PGRST301' ||
          body?.message?.includes('JWT expired')
        ) {
          console.warn('[Supabase] JWT clock skew detected, retrying after 1s...');
          await new Promise((r) => setTimeout(r, 1000));
          return fetchWithRetry(url, init, 2);
        }
      } catch {
        // ignore JSON parse errors
      }
    }

    // Handle invalid/expired refresh token — don't retry, let the auth layer handle it
    if (res.status === 400 && isAuth) {
      const cloned = res.clone();
      try {
        const body = await cloned.json();
        if (
          typeof body?.error === 'string' &&
          (body.error.includes('Refresh Token Not Found') || body.error.includes('Invalid Refresh Token'))
        ) {
          console.warn('[Supabase] Refresh token is invalid or revoked. Session will be cleared.');
          // Don't retry — the token is dead, let the caller handle sign-out
        }
      } catch {
        // ignore JSON parse errors
      }
    }

    return res;
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      const msg = isAuth
        ? 'Connection timed out while refreshing your session. Please check your internet connection and try again.'
        : 'Connection to server timed out. Please check your network or try again later.';
      throw new Error(msg);
    }
    connectionStatus = 'failed';
    throw err;
  }
}

try {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('[Supabase] URL or Anon Key not configured. Using dummy client.');
    connectionStatus = 'failed';
    supabaseInstance = createDummyClient();
  } else {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
      global: {
        fetch: fetchWithRetry as any,
      },
    });
  }
} catch (err) {
  console.error('[Supabase] Failed to create client:', err);
  connectionStatus = 'failed';
  supabaseInstance = createDummyClient();
}

export const supabase = supabaseInstance;
export const isSupabaseConnected = () => connectionStatus === 'ok';
export const getConnectionStatus = () => connectionStatus;

export async function checkSupabaseConnection(): Promise<boolean> {
  if (!supabaseUrl || supabaseUrl === 'http://localhost') return false;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    // Use properties endpoint which allows public (anon) access — avoids 401 noise in console
    const res = await fetch(`${supabaseUrl}/rest/v1/properties?limit=1`, {
      method: 'GET',
      headers: { apikey: supabaseAnonKey || '' },
      signal: controller.signal,
    });
    clearTimeout(timeout);
    const ok = res.status >= 200 && res.status < 300;
    connectionStatus = ok ? 'ok' : 'failed';
    return ok;
  } catch {
    connectionStatus = 'failed';
    return false;
  }
}